import React, { Component } from 'react'
import {
	Alert,
	Layout,
	Button,
	Result,
	Row,
	Col,
	Typography,
	Collapse,
	notification
} from 'antd'
import _ from 'lodash'
import { injectIntl, FormattedMessage } from 'react-intl'
import { Query } from '@apollo/client/react/components'
import { Redirect } from 'react-router-dom'
import gql from 'graphql-tag'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGhost } from '@fortawesome/free-solid-svg-icons'

import { Mobile, Desktop, Tablet } from '../../../components/Responsive'
import Loading from '../../../components/LoadingPage'
import * as Utils from '../../../components/Utils'
import Player from './Player'
import * as AudioPlayer from '../../../components/AudioPlayer'
import SubmitModal from './Submit'
import Logo from '../../../components/SpectreLogo'
import TrackList from './List'
import { Colors } from '../../../components/Styles'
import { getAudioDuration } from '../../../utils/audio'
import logoSpectre from '../../../img/logoSpectre.png'

import { publicGraphqlQueries } from '../../../shared/publicGraphqlQuery'

const SampleQuery = gql`
	${publicGraphqlQueries.queryFetchSampleToListen}
`

class IListenDisplay extends Component {
	state = {
		selectedTrack: null,
		selectedIndex: 0,
		feedback: {},
		hasChanges: false,
		playing: false,
		submitModal: false,
		submitted: false,
		showList: false,
		shaking: false,
		// used in mobile to collapse the music concept when the user starts the playback
		playbackHasStarted: false,
		hideMusicConcept: undefined
	}

	// in mobile, when the review starts, the music concept is displayed by default
	shouldDisplayMusicConcept = () => {
		if (_.isNil(this.state.hideMusicConcept)) {
			return !this.state.playbackHasStarted
		}
		return !this.state.hideMusicConcept
	}

	// use to show to the user where the submit button is
	triggerSubmitButtonShake = () => {
		this.setState({ shaking: true }, () => {
			setTimeout(() => this.setState({ shaking: false }), 2000)
		})
	}

	openNotification = () => {
		const { intl } = this.props
		const key = `open${Date.now()}`
		notification.open({
			key,
			duration: 5,
			className: 'dark-notification',
			message: intl.formatMessage({ id: 'samples.notification.title' }),
			description: intl.formatMessage({
				id: 'samples.notification.description'
			}),
			closeIcon: <div style={{ fontSize: 30 }}>⬆︎</div>,
			onClick: () => {
				notification.close(key)
			}
		})
	}

	onBeforeUnload = (event) => {
		const { intl } = this.props
		// Ask confirmation if the user close the window after the fields have been edited
		if (
			!this.state.submitted &&
			Object.keys(this.state.feedback).length > 0
		) {
			event.preventDefault()
			event.returnValue = intl.formatMessage({
				id: 'samples.close.confirmation.message'
			})

			setTimeout(() => {
				this.triggerSubmitButtonShake()
			})
		}
	}

	componentDidMount() {
		const fb = localStorage.getItem(`sample-${this.props.sample._id}`)
		if (fb) {
			this.setState({ feedback: JSON.parse(fb) })
		}
		this.preloadTracks()
		window.addEventListener('beforeunload', this.onBeforeUnload)

		const { review } = this.props
		if (review) {
			this.openNotification()
		}
	}

	componentWillUnmount() {
		window.removeEventListener('beforeunload', this.onBeforeUnload)
	}

	preloadTracks = () => {
		const { sample } = this.props
		sample.trackObjects.forEach((trackObject) => {
			AudioPlayer.preloadTrack({
				track: trackObject._id
			})
		})
	}

	selectTrack = async (index) => {
		const { sample, review } = this.props
		const track = sample.tracks[index]
		const trackObject = sample.trackObjects[index]
		const trackDuration = getAudioDuration(trackObject) / 1000

		AudioPlayer.startTrack({
			track: trackObject._id,
			crossfade: 2,
			startTime: track.start === -1 ? 0 : track.start,
			endTime: track.end === -1 ? trackDuration : track.end,
			endCallback: () => {
				if (Utils.isClientIOS()) {
					// on iOS we can't play a track without user interaction
					this.setState({ playing: false })
				} else {
					// if we are in review mode, we stop at the end of the track to let the user give feedback
					// if no review, we loop between the tracks
					if (!review) {
						this.selectTrack((index + 1) % sample.tracks.length)
					} else {
						// we stop the song when the player tell us it as reach the endTime
						// otherwise the player will continue until the end of the track
						AudioPlayer.stop(2)
						setTimeout(() => {
							this.setState({ playing: false })
						}, 2000)
					}
				}
			}
		})

		this.setState({
			selectedTrack: {
				...track,
				...trackObject
			},
			selectedIndex: index,
			playing: true
		})
	}

	// update the feedback for the current track
	// if rating/comment or reason are null, they will be deleted
	setFeedback = (rating, comment, reason) => {
		let trackFb = this.state.feedback[this.state.selectedTrack._id] || {}
		if (rating) {
			if (trackFb.rating === rating) {
				trackFb.rating = null
			} else {
				trackFb.rating = rating
			}
		}
		if (comment || comment === '' || comment === null) {
			trackFb.comment = comment
		}

		// if we pass null, we reset the reason
		if (reason || reason === null) {
			trackFb.reason = reason
		}

		let fb = { ...this.state.feedback }
		fb[this.state.selectedTrack._id] = trackFb
		this.setState({ feedback: fb, hasChanges: true })

		this.save()
	}

	playPause = () => {
		if (this.state.playing) {
			AudioPlayer.stop()
			this.setState({ playing: false })
		} else {
			// on the first play on mobile, if the music concept is still displayed
			if (!this.state.playbackHasStarted) {
				// if the music concept is still displayed, we hide it at the first playback to avoid the user to have to close it manually
				if (!this.state.hideMusicConcept) {
					this.setState({ hideMusicConcept: true })
				}
				this.setState({ playbackHasStarted: true })
			}

			this.selectTrack(this.state.selectedIndex)
		}
	}

	save = () => {
		localStorage.setItem(
			`sample-${this.props.sample._id}`,
			JSON.stringify(this.state.feedback)
		)
	}

	// ==== RENDERING BITS TO MUTUALIZE RESPONSIVE RENDERING ====
	renderSubmitButton = (messageId) => {
		if (!this.props.review) return <div />
		return (
			<Button
				ghost
				type="ghost"
				onClick={() => this.setState({ submitModal: true })}
				className={
					this.state.shaking ? 'shake pulse-button' : 'pulse-button'
				}
			>
				<FormattedMessage id={messageId} />
			</Button>
		)
	}

	renderTrackList = (mobile = false) => {
		const { sample, review } = this.props
		return (
			<TrackList
				sample={sample}
				feedback={this.state.feedback}
				review={review}
				selectTrack={this.selectTrack}
				selectedIndex={this.state.selectedIndex}
				mobile={mobile}
			/>
		)
	}

	renderPlayer = (mobile) => {
		const { sample, review } = this.props
		const extraProps =
			mobile ?
				{
					mobile: true,
					showList: () => this.setState({ showList: true })
				}
			:	{}

		const handleNext = () => {
			// if we expect review from the user and its the last track
			// we open the submission modal
			if (
				review &&
				this.state.selectedIndex + 1 === sample.tracks.length
			) {
				this.setState({ submitModal: true })
			}
			this.selectTrack(
				(this.state.selectedIndex + 1) % sample.tracks.length
			)
		}

		return (
			<Player
				sample={sample}
				track={this.state.selectedTrack}
				next={handleNext}
				playPause={this.playPause}
				feedback={
					this.state.selectedTrack ?
						this.state.feedback[this.state.selectedTrack._id]
					:	null
				}
				setFeedback={this.setFeedback}
				playing={this.state.playing}
				review={review}
				{...extraProps}
			/>
		)
	}

	renderStyles = () => {
		const { sample } = this.props
		return (
			<style>{`
				.ant-notification-topRight { top: 60px !important; }
 
				.dark-notification.ant-notification-notice {
					background: ${colors.bg} !important;
					box-shadow: 0px 0px 10px ${colors.subtitle};
				}
				.dark-notification .ant-notification-notice-message,
				.dark-notification .ant-notification-notice-description,
				.dark-notification .ant-notification-notice-close,
				.dark-notification .ant-notification-notice-close-x .anticon {
					color: ${colors.text} !important;
				}
 
				.pulse-button {
					position: relative;
					overflow: visible;
				}
				.pulse-button::after {
					content: "";
					position: absolute;
					inset: 0;
					border-radius: inherit;
					background: #fff;
					z-index: -1;
					animation: pulse-wave 3s linear infinite;
				}
				@keyframes pulse-wave {
					0%   { transform: scale(1);                 opacity: 0.3; }
					60%  { transform: scaleX(1.2) scaleY(2);    opacity: 0; }
					100% { transform: scaleX(1.2) scaleY(2);    opacity: 0; }
				}
 
				@keyframes shake {
					0%, 100% { transform: translateX(0)   rotate(0.3deg); }
					10%      { transform: translateX(-2px) rotate(-0.3deg); }
					20%      { transform: translateX(0px)  rotate(0.3deg); }
					30%      { transform: translateX(2px)  rotate(-0.3deg); }
					40%      { transform: translateX(0px)  rotate(0.3deg); }
					50%      { transform: translateX(-2px) rotate(-0.3deg); }
					60%      { transform: translateX(0px)  rotate(0.3deg); }
					70%      { transform: translateX(2px)  rotate(-0.3deg); }
					80%      { transform: translateX(0px)  rotate(0.3deg); }
					90%      { transform: translateX(-2px) rotate(-0.3deg); }
				}
				.shake {
					animation: shake 1s ease-in-out infinite;
					transform-origin: center center;
				}
 
				.withBackgroundImage { position: relative; }
				.withBackgroundImage::before {
					content: '';
					position: absolute;
					inset: 0;
					background-image: url('${Utils.sampleImageUrl(sample)}');
					background-size: cover;
					background-position: center;
					background-repeat: no-repeat;
					opacity: 0.3;
					z-index: 0;
					pointer-events: none;
					filter: blur(4px);
				}
				.withBackgroundImage > * {
					position: relative;
					z-index: 1;
				}
 
				.dark-collapse .ant-collapse-ghost,
				.dark-collapse .ant-collapse-ghost .ant-collapse-item {
					background: transparent !important;
				}
				.dark-collapse .ant-collapse-ghost .ant-collapse-item {
					overflow: auto;
					max-height: 50vh;
				}
				.dark-collapse .ant-collapse-ghost .ant-collapse-arrow {
					color: ${colors.subtitle} !important;
				}
 
				.dark-track-list table {
					background: ${colors.bg} !important;
				}
			`}</style>
		)
	}

	// shared between mobile (Footer) and tablet (Row/Col)
	renderMusicConceptCollapse = () => {
		const isOpen = this.shouldDisplayMusicConcept()
		return (
			<div className="dark-collapse">
				<Collapse
					activeKey={isOpen ? ['notes'] : []}
					ghost
					style={{ background: colors.bg }}
				>
					<Collapse.Panel
						style={{
							color: colors.subtitle,
							background: colors.bg
						}}
						key="notes"
						header={
							<span style={{ color: colors.subtitle }}>
								<FormattedMessage
									id={
										isOpen ?
											'samples.listen.description.hide'
										:	'samples.listen.description.show'
									}
								/>
							</span>
						}
					>
						<Typography.Text
							style={{
								fontSize: 16,
								whiteSpace: 'pre-line',
								color: colors.subtitle
							}}
						>
							{this.props.sample.description}
						</Typography.Text>
					</Collapse.Panel>
				</Collapse>
			</div>
		)
	}

	// ==== GLOBAL RENDERING METHOD ====

	render() {
		const { sample, intl, review } = this.props

		if (this.state.submitted) {
			return (
				<Layout style={styles.main}>
					<Layout.Content style={styles.layout}>
						<Result
							status="success"
							title={
								<span style={{ color: colors.text }}>
									<FormattedMessage id="samples.listen.submitSuccess" />
								</span>
							}
						/>
					</Layout.Content>
				</Layout>
			)
		}

		Utils.setPageTitle(
			intl,
			null,
			intl.formatMessage(
				{ id: 'samples.listen.title' },
				{ name: sample.name }
			)
		)

		return (
			<React.Fragment>
				{this.renderStyles()}
				<Mobile>
					<Layout style={styles.main}>
						<Layout.Header
							style={{ ...styles.header, padding: '0 10px' }}
						>
							{!this.state.showList ?
								<div
									style={styles.mobileLogoContainer}
									onClick={() => {
										this.setState({ showList: false })
									}}
								>
									<Logo color="#FFF" />
								</div>
							:	<Button
									type="secondary"
									onClick={() =>
										this.setState({ showList: false })
									}
								>
									Back
								</Button>
							}
							<h1 style={styles.headerTitle}>{sample.name}</h1>

							{this.renderSubmitButton('actions.submit')}
						</Layout.Header>

						<Layout.Content
							style={{ ...styles.layout, position: 'relative' }}
							className={
								sample.image ? 'withBackgroundImage' : ''
							}
						>
							{this.state.showList ?
								<>
									<style>{`
										.dark-track-list table {
											background: ${colors.bg} !important;
										}
									`}</style>
									<div className="dark-track-list">
										{this.renderTrackList(true)}
									</div>
								</>
							:	this.renderPlayer(true)}
						</Layout.Content>

						{sample.description && (
							<Layout.Footer
								onClick={() => {
									this.setState({
										hideMusicConcept:
											!this.state.hideMusicConcept
									})
								}}
								style={{
									textAlign: 'left',
									padding: 0,
									backgroundColor: colors.bg,
									color: colors.subtitle
								}}
							>
								{this.renderMusicConceptCollapse()}
							</Layout.Footer>
						)}
					</Layout>
				</Mobile>
				<Tablet>
					<Layout style={styles.main}>
						<Layout.Header style={styles.header}>
							<img
								src={logoSpectre}
								alt="Spectre"
								style={{ height: '40%' }}
							/>
							<h1 style={styles.headerTitle}>{sample.name}</h1>

							{this.renderSubmitButton('samples.submit')}
						</Layout.Header>
						<Layout.Content style={styles.layout}>
							{sample.description && (
								<Row>
									<Col
										span={24}
										onClick={() => {
											this.setState({
												hideMusicConcept:
													!this.state.hideMusicConcept
											})
										}}
										style={{
											textAlign: 'left',
											padding: 0,
											backgroundColor: colors.bg,
											color: colors.subtitle
										}}
									>
										{this.renderMusicConceptCollapse()}
									</Col>
								</Row>
							)}
							<Row
								style={{ position: 'relative' }}
								className={
									sample.image ? 'withBackgroundImage' : ''
								}
							>
								<Col span={24}>{this.renderPlayer()}</Col>
							</Row>
							<Row>
								<div style={styles.list}>
									{this.renderTrackList()}
								</div>
							</Row>
						</Layout.Content>
					</Layout>
				</Tablet>
				<Desktop>
					<Layout style={styles.main}>
						<Layout.Header style={styles.header}>
							<img
								src={logoSpectre}
								alt="Spectre"
								style={{ height: '40%' }}
							/>
							<h1 style={styles.headerTitle}>{sample.name}</h1>

							{this.renderSubmitButton('samples.submit')}
						</Layout.Header>
						<Layout.Content style={styles.layout}>
							<Row
								style={{ position: 'relative' }}
								className={
									sample.image ? 'withBackgroundImage' : ''
								}
							>
								<Col span={sample.description ? 18 : 24}>
									{this.renderPlayer()}
								</Col>

								{sample.description && (
									<Col
										span={6}
										style={{
											display: 'flex',
											alignItems: 'center'
										}}
									>
										<div style={{ paddingRight: 10 }}>
											<Typography.Title
												style={{
													color: colors.subtitle,
													paddingBottom: '10px',
													borderBottom: `2px solid ${colors.subtitle}`
												}}
											>
												<FormattedMessage id="samples.listen.description.title" />
											</Typography.Title>
											<Typography.Text
												style={{
													display: 'inline-block',
													maxHeight: '300px',
													overflow: 'auto',
													color: colors.subtitle,
													textAlign: 'left',
													whiteSpace: 'pre-line'
												}}
											>
												{sample.description}
											</Typography.Text>
										</div>
									</Col>
								)}
							</Row>
							<Row>
								<div style={styles.list}>
									{this.renderTrackList()}
								</div>
							</Row>
						</Layout.Content>
					</Layout>
				</Desktop>
				{this.state.submitModal && (
					<SubmitModal
						sample={sample}
						feedback={this.state.feedback}
						onClose={(didSubmit) => {
							this.setState({
								submitModal: false,
								submitted: didSubmit
							})
							if (this.state.playing) {
								this.playPause()
							}
						}}
						mutation={({ email, name, comment }) => ({
							variables: {
								email,
								name,
								comment,
								tracks: sample.tracks.map((t) => {
									const fb = this.state.feedback[t.trackId]
									return {
										trackId: t.trackId,
										...fb
									}
								}),
								sampleId: sample._id,
								date: new Date()
							},
							mutation: gql`
								${publicGraphqlQueries.mutationCreateSampleReview}
							`
						})}
					/>
				)}
			</React.Fragment>
		)
	}
}

const ListenDisplay = injectIntl(IListenDisplay)

class Listen extends Component {
	isSampleGone(error) {
		return (error?.graphQLErrors ?? []).some(
			(e) => e.extensions?.code === 'GONE'
		)
	}

	render() {
		const sample = this.props.match.params.sampleId
		return (
			<div style={styles.main}>
				{sample && (
					<Query query={SampleQuery} variables={{ id: sample }}>
						{({ loading, error, data }) => {
							if ((!data || !data.sampleById) && loading) {
								return <Loading />
							}
							if (error) {
								if (this.isSampleGone(error)) {
									// special case, we specific message
									return (
										<>
											<style>{`
												@keyframes ghost-wander {
													0% {
														transform: translateX(0) translateY(0) rotate(0deg);
														opacity: 0.8;
													}
													12.5% {
														transform: translateX(-25px) translateY(-6px) rotate(-3deg);
														opacity: 0.5;
													}
													25% {
														transform: translateX(-50px) translateY(0px) rotate(3deg);
														opacity: 0.2;
													}
													37.5% {
														transform: translateX(-25px) translateY(-8px) rotate(-2deg);
														opacity: 0.5;
													}
													50% {
														transform: translateX(0) translateY(0) rotate(0deg);
														opacity: 0.8;
													}
													62.5% {
														transform: translateX(25px) translateY(10px) rotate(3deg);
														opacity: 0.5;
													}
													75% {
														transform: translateX(50px) translateY(-5px) rotate(-3deg);
														opacity: 0.2;
													}
													87.5% {
														transform: translateX(25px) translateY(3px) rotate(2deg);
														opacity: 0.5;
													}
													100% {
														transform: translateX(0) translateY(0) rotate(0deg);
														opacity: 0.8;
													}
												}

												.ghost-icon {
													animation: ghost-wander 30s linear infinite;
												}
											`}</style>
											<Result
												style={{
													paddingTop: 50
												}}
												status="error"
												icon={
													<FontAwesomeIcon
														className="ghost-icon"
														icon={faGhost}
														style={{
															fontSize: 150,
															color: Colors.emptyIcons,
															margin: 20
														}}
													/>
												}
												title={
													<div
														style={{
															color: colors.text,
															marginBottom: 30
														}}
													>
														<FormattedMessage id="samples.listen.errors.gone.title" />
													</div>
												}
												subTitle={
													<div
														style={{
															color: colors.text,
															marginBottom: 30
														}}
													>
														<FormattedMessage
															id="samples.listen.errors.gone.subTitle"
															values={{
																email: (
																	chunks
																) => (
																	<a
																		href={`mailto:${chunks}`}
																	>
																		{chunks}
																	</a>
																)
															}}
														/>
													</div>
												}
											></Result>
										</>
									)
								}

								return (
									<Alert
										message={this.props.intl.formatMessage({
											id: 'error'
										})}
										description={this.props.intl.formatMessage(
											{ id: 'error.message' }
										)}
										type="error"
										showIcon
										banner
									/>
								)
							}

							if (data.sampleById) {
								return (
									<ListenDisplay
										sample={data.sampleById}
										review={this.props.review}
									/>
								)
							} else {
								return <Redirect to="/error/404/NOT_FOUND" />
							}
						}}
					</Query>
				)}
			</div>
		)
	}
}

export default injectIntl(Listen)

const colors = {
	bg: '#181818',
	primary: '#1890ff',
	text: '#fff',
	subtitle: '#ccc',
	yes: '#52c41a',
	no: '#f5222d'
}

const styles = {
	main: {
		height: '100vh',
		width: '100vw',
		background: colors.bg,
		color: colors.text
	},
	layout: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		height: '100%',
		overflowY: 'auto',
		paddingTop: 64
	},
	header: {
		position: 'fixed',
		zIndex: 10,
		width: '100%',
		background: colors.bg,
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center'
	},
	headerTitle: {
		color: '#999',
		fontWeight: 700,
		textTransform: 'uppercase',
		fontSize: '16px',
		lineHeight: '20px',
		letterSpacing: '3.2px',
		margin: 0
	},
	list: {
		top: 464,
		left: 0,
		right: 0,
		bottom: 0,
		width: '100%',
		overflowY: 'auto'
	},
	title: {
		color: colors.text,
		textAlign: 'center'
	},
	mobileLogoContainer: {
		marginLeft: 3,
		marginRight: 6,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		float: 'left',
		width: 50
	}
}
